import React from 'react';
import { ButtonTertiary, MeatballMenu, Numberbox, Textarea } from 'ui';
import * as navigate from 'app/area/shared/navigation';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { SelectSingle } from 'app/area/shared/translated-components';
import { CompanyReportFormIds } from '../constants';
import { endpoints } from '../endpoints';
import { TextsItemProps } from '../types';
import { cellMaxLength, rangeMaxLength, textIdDebounceTime } from 'app/area/company-reports-administration/form/components/constants';
import { SHORT_DEBOUNCE_TIME } from 'app/components/translation/constants';
import { TextValue } from 'app/area/company-reports-administration/form/types-store';
import { getOnlyExistedValues } from 'app/area/company-reports-administration/form/components/helpers';

import styles from './texts-item.scss';

// tslint:disable max-func-body-length
export const TextsItem: React.FC<TextsItemProps> = ({
  value, options, locale, validation, textsValidation, translation, removeAllTexts, translationAdminAccess,
  updateTextItem, resetTextValidation, resetTextsValidation, loadOptions, updateTextItemDescription, removeText
}) => {
  const id = value?.id;
  const resetAllTextValidation = (key: keyof TextValue) => {
    resetTextValidation(id, key);
    resetTextsValidation(id);
  };

  const onTextSetChange = (textSet: ComplexValue<number>) => {
    resetAllTextValidation(CompanyReportFormIds.textSet);
    resetAllTextValidation(CompanyReportFormIds.textDescription);

    updateTextItem({ ...value, textSet: getOnlyExistedValues(textSet) });

    const textIdValue = value?.textId;
    textIdValue && updateTextItemDescription(id, textIdValue, textSet?.value);
  };

  const onTextIdChange = (textId: number) => {
    resetAllTextValidation(CompanyReportFormIds.textId);
    resetAllTextValidation(CompanyReportFormIds.textDescription);

    updateTextItem({ ...value, textId });

    const textSetValue = value?.textSet?.value;
    textSetValue && updateTextItemDescription(id, textId, textSetValue);
  };

  const onTextSetLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(CompanyReportFormIds.textSet, endpoints.textSetAllOptions, loadOptionsParams);
  };

  const onManageText = () => {
    navigate.toTextItem(value?.textId, value?.textSet?.value);
  };

  const onRemove = () => {
    resetTextValidation(id);
    resetTextsValidation(id);
    removeText(id);
  };

  const onCellChange = (cell: string) => {
    resetTextValidation(id, CompanyReportFormIds.cell);
    updateTextItem({ ...value, cell });
  };
  const onRangeChange = (range: string) => {
    updateTextItem({ ...value, range });
  };

  const textValidation = (field: string) => validation?.text?.length > 0 ? validation?.text.find(validationError =>
    validationError.messages.find(message =>
      message.parameters?.id === id && message.parameters?.field === field
    )) : undefined;

  return <div className="row">
    <div className="col-md-12" >
      <div className={styles.container}>
        <MeatballMenu closeAfterClick={true}>
          <li>
            <button type="button" className="dropdown-item" onClick={onRemove}>
              {translation.remove}
            </button>
          </li>
          <li>
            <button type="button" className="dropdown-item" onClick={removeAllTexts}>
              {translation.remove_all}
            </button>
          </li>
        </MeatballMenu>
        <div className="row mt-3">
          <div className="col-lg-3 col-sm-12">
            <SelectSingle
              componentId={CompanyReportFormIds.textSet}
              className={undefined}
              label={{ text: translation.email_settings_text_set, required: true }}
              valueItem={value?.textSet}
              onChange={onTextSetChange}
              validation={textValidation(CompanyReportFormIds.textSet)}
              placeholder={translation?.search}
              options={options?.textSet?.codes as ComplexValue<number>[] || []}
              fetching={options?.textSet?.fetching}
              loadOptions={undefined}
              onLoadOptions={onTextSetLoadOptions} />
          </div>
          <div className="col-lg-3 col-sm-12">
            <Numberbox
              componentId={CompanyReportFormIds.textId}
              label={translation ? { text: translation.email_settings_text_id, required: true } : undefined}
              value={value?.textId}
              onChange={onTextIdChange}
              useThousand=" "
              maxValue={999999999}
              locale={locale}
              validation={textValidation(CompanyReportFormIds.textId)}
              textInvalidRangeError={translation.must_be_within_range}
              wait={textIdDebounceTime} />
          </div>
          <div className="col-lg-4 col-sm-12">
            <Textarea
              componentId={CompanyReportFormIds.textDescription}
              readOnly={true}
              value={value?.textDescription}
              label={{ text: translation.email_text_description, required: true }}
              validation={textValidation(CompanyReportFormIds.textDescription) || textsValidation?.[id]?.textDescription}
            />
          </div>
          {translationAdminAccess &&
            <div className={`col-lg-2 col-sm-12 ${styles.buttonWrapper}`}>
              <ButtonTertiary
                disabled={!value?.textDescription}
                onClick={onManageText}>
                {translation.email_manage_text}
              </ButtonTertiary>
            </div>}
        </div>
        <div className="row mt-3">
          <div className="col-lg-6 col-sm-12">
            <Textarea
              onChange={onCellChange}
              value={value.cell}
              placeholder={translation.cell_placeholder}
              label={{ text: translation.cell, required: true }}
              validation={textValidation(CompanyReportFormIds.cell)}
              wait={SHORT_DEBOUNCE_TIME}
              maxLength={cellMaxLength} />
          </div>
          <div className="col-lg-6 col-sm-12">
            <Textarea
              onChange={onRangeChange}
              value={value.range}
              label={{ text: translation.range }}
              info={{ visible: true, text: translation.range_info_text }}
              wait={SHORT_DEBOUNCE_TIME}
              maxLength={rangeMaxLength} />
          </div>
        </div>
      </div>
    </div>
  </div>;
};
